import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { FacetDict } from '~~/shared/types/common'
import { useLocaleStore } from '~/stores/localeStore'

const hideTaiaAndQuizCard = ref(false)
const hideAction = ref(false)

export const useProducts = () => {
  const router = useRouter()
  const route = useRoute()
  const localeStore = useLocaleStore()

  const loading = ref(false)
  const facetsLoading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const isInitializing = ref(false)
  const facetsUiVersion = ref(0)
  const isCheckproductData = ref(true)

  const parseFilters = (): Record<string, string[]> => {
    const filters: Record<string, string[]> = {}

    for (const [key, queryValue] of Object.entries(route.query)) {
      if (!key.startsWith('f[')) continue

      const values = Array.isArray(queryValue) ? queryValue : [queryValue]
      for (const rawFilter of values) {
        if (typeof rawFilter !== 'string') continue

        const separatorIndex = rawFilter.indexOf('_')
        if (separatorIndex < 1 || separatorIndex === rawFilter.length - 1) continue

        const facetName = rawFilter.slice(0, separatorIndex)
        const facetValue = rawFilter.slice(separatorIndex + 1)
        ;(filters[facetName] ??= []).push(facetValue)
      }
    }

    return filters
  }

  const buildQuery = (filters: Record<string, string[]>) => {
    const query = Object.fromEntries(
      Object.entries(route.query).filter(([key]) => !key.startsWith('f[') && key !== 'page')
    )
    let index = 0

    for (const [facetName, values] of Object.entries(filters)) {
      for (const value of values) query[`f[${index++}]`] = `${facetName}_${value}`
    }

    return query
  }

  const cleanValueForFacet = (facetName: string, raw: string, facets: FacetDict): string | null => {
    if (!raw) return null

    let value = raw
    const prefix = `${facetName}_`
    while (value.startsWith(prefix)) value = value.slice(prefix.length)

    const allowedValues = new Set(facets[facetName]?.values.map(facetValue => facetValue.value) ?? [])
    return allowedValues.has(value) ? value : null
  }

  let updateTimer: ReturnType<typeof setTimeout> | null = null
  let pendingFilters: Record<string, string[]> | null = null
  let filterRevision = 0
  let navigationPromise: Promise<unknown> | null = null
  let flushRequested = false

  const flushPendingFilters = async () => {
    if (!pendingFilters) return
    if (navigationPromise) {
      flushRequested = true
      return
    }

    const revision = filterRevision
    const filters = pendingFilters
    navigationPromise = Promise.resolve(router.push({ path: route.path, query: buildQuery(filters) }))

    try {
      await navigationPromise
    } finally {
      navigationPromise = null

      if (revision === filterRevision) pendingFilters = null

      const shouldFlushAgain = Boolean(pendingFilters) && (flushRequested || revision !== filterRevision)
      flushRequested = false
      if (shouldFlushAgain && !updateTimer) await flushPendingFilters()
    }
  }

  const scheduleUpdate = () => {
    if (updateTimer) clearTimeout(updateTimer)
    updateTimer = setTimeout(() => {
      updateTimer = null
      void flushPendingFilters()
    }, 200)
  }

  const setFacetValues = (facetName: string, facets: FacetDict, values: string[]) => {
    const normalizedValues = values
      .map(value => cleanValueForFacet(facetName, value, facets))
      .filter((value): value is string => Boolean(value))

    pendingFilters ??= parseFilters()
    filterRevision++
    if (normalizedValues.length) {
      pendingFilters[facetName] = normalizedValues
    } else {
      delete pendingFilters[facetName]
    }
    scheduleUpdate()
  }

  const clearAllFacets = async () => {
    if (updateTimer) clearTimeout(updateTimer)
    updateTimer = null
    pendingFilters = null
    filterRevision++
    flushRequested = false
    facetsUiVersion.value++

    if (process.client) {
      if (navigationPromise) await navigationPromise
      await router.replace({ path: route.path, query: buildQuery({}) })
    }
  }

  const initFromUrl = async () => {
    if (initialized.value || isInitializing.value) return

    isInitializing.value = true
    try {
      if (!localeStore.locales?.length) await localeStore.fetchLocales?.()
      initialized.value = true
    } catch (localeError) {
      console.error('[useProducts] Unable to load locales; using the fallback locale.', localeError)
      initialized.value = true
    } finally {
      isInitializing.value = false
    }
  }

  const hideCardsInPLP = (data: { value: boolean, action: boolean }) => {
    hideTaiaAndQuizCard.value = data.value
    hideAction.value = data.action
  }

  return {
    loading,
    facetsLoading,
    error,
    initialized,
    facetsUiVersion,
    hideTaiaAndQuizCard,
    hideAction,
    isCheckproductData,
    initFromUrl,
    setFacetValues,
    clearAllFacets,
    hideCardsInPLP
  }
}
